import Phaser from 'phaser';
import {
  GEODE_DEFS,
  geodeCrackFrameIndex,
  geodeCrackSheetKey,
} from '../../geode';
import type { GeodeModel } from '../types';
import { geodeLayoutFromModel } from './geode-model';
import { GEODE_BREAK_TIMING, playGeodeBreakVfx } from './geode-break';
import { flashGeodeCrack, playGeodeHitReaction } from './geode-hit-fx';
import { bindGeodePixelHit } from './geode-hit-test';

export type GeodeViewState = {
  isBroken: () => boolean;
  isRespawning: () => boolean;
};

export function createGeodeView(
  scene: Phaser.Scene,
  getModel: () => GeodeModel,
  getState: GeodeViewState,
  onRespawnRequested: () => void,
  onRespawnTweenComplete: () => void,
) {
  let panelVisible = true;

  // Geode sits above Boogatooth (depth 10); weapon swing uses 13.
  const shadow = scene.add
    .image(0, 0, 'geode-shadow')
    .setAlpha(0.85)
    .setDepth(11);

  const wiggle = scene.add.container(0, 0).setDepth(12);
  const squash = scene.add.container(0, 0);
  const sprite = scene.add
    .image(0, 0, geodeCrackSheetKey(getModel().type), geodeCrackFrameIndex(1))
    .setOrigin(0.5, 0.5);
  squash.add(sprite);
  wiggle.add(squash);
  bindGeodePixelHit(sprite);

  function layout() {
    return geodeLayoutFromModel(getModel());
  }

  function resetPose() {
    wiggle.setAngle(0);
    squash.setScale(1);
  }

  function applyLayout() {
    const { cx, cy, display, shadow: shadowLayout } = layout();
    wiggle.setPosition(cx, cy);
    shadow
      .setPosition(shadowLayout.x, shadowLayout.y)
      .setDisplaySize(shadowLayout.width, shadowLayout.width * 0.12);
    sprite.setDisplaySize(display, display);
  }

  function applyCrackFrame(frame: number, frameAdvanced: boolean) {
    const model = getModel();
    const { display } = layout();
    const sheetKey = geodeCrackSheetKey(model.type);
    if (scene.textures.exists(sheetKey)) {
      sprite.setTexture(sheetKey, geodeCrackFrameIndex(frame));
      sprite.setDisplaySize(display, display);
      sprite.setVisible(true);
    }
    if (frameAdvanced) {
      flashGeodeCrack(scene, sprite);
    }
  }

  function syncVisibility() {
    if (breakSequenceActive || respawnSequenceActive) return;
    const visible = panelVisible && !getState.isBroken();
    wiggle.setVisible(visible);
    shadow.setVisible(visible);
  }

  let breakSequenceActive = false;
  let respawnSequenceActive = false;

  async function playBreakSequence() {
    const { cx, cy, display } = layout();
    const model = getModel();

    breakSequenceActive = true;
    scene.tweens.killTweensOf(wiggle);
    scene.tweens.killTweensOf(squash);
    resetPose();

    const maxFrame = GEODE_DEFS[model.type]?.frames ?? 7;
    applyCrackFrame(maxFrame, false);
    wiggle.setPosition(cx, cy);
    wiggle.setAlpha(1);
    wiggle.setVisible(true);
    shadow.setVisible(true);

    scene.tweens.add({ targets: shadow, alpha: 0, duration: 600 });

    await playGeodeBreakVfx(scene, { x: cx, y: cy, display }, model.type, {
      crackSprite: sprite,
    });

    scene.time.delayedCall(GEODE_BREAK_TIMING.postBreakSettleMs, () => {
      scene.tweens.add({
        targets: wiggle,
        alpha: 0,
        duration: GEODE_BREAK_TIMING.fadeOutMs,
        onComplete: () => {
          wiggle.setVisible(false);
          scene.time.delayedCall(GEODE_BREAK_TIMING.postFadeMs, () => {
            breakSequenceActive = false;
            onRespawnRequested();
          });
        },
      });
    });
  }

  function startRespawnTween() {
    const { cx, cy } = layout();
    respawnSequenceActive = true;
    resetPose();
    scene.tweens.killTweensOf(wiggle);
    scene.tweens.killTweensOf(shadow);
    applyCrackFrame(1, false);
    wiggle.setPosition(cx, cy - 200);
    wiggle.setAlpha(0);
    wiggle.setVisible(true);
    shadow.setVisible(true);
    shadow.setAlpha(0);

    scene.tweens.add({
      targets: wiggle,
      y: cy,
      alpha: 1,
      duration: GEODE_BREAK_TIMING.respawnDropMs,
      ease: 'Back.easeOut',
      onComplete: () => {
        wiggle.setPosition(cx, cy);
        wiggle.setAlpha(1);
        resetPose();
        respawnSequenceActive = false;
        onRespawnTweenComplete();
        syncVisibility();
      },
    });
    scene.tweens.add({
      targets: shadow,
      alpha: 0.85,
      duration: GEODE_BREAK_TIMING.respawnDropMs,
    });
  }

  function setPanelVisible(rightPanelVisible: boolean) {
    panelVisible = rightPanelVisible;
    syncVisibility();
  }

  function setModel() {
    applyLayout();
    syncVisibility();
  }

  applyLayout();

  return {
    sprite,
    applyCrackFrame,
    playBreakSequence,
    setPanelVisible,
    setModel,
    isVisible: () => panelVisible && !getState.isBroken(),
    onHitReaction(pointer: Phaser.Input.Pointer) {
      const { cx } = layout();
      playGeodeHitReaction(scene, {
        wiggle,
        squash,
        cx,
        clientX: pointer.worldX,
        respawning: getState.isRespawning(),
      });
    },
    onRespawnStart() {
      resetPose();
    },
    startRespawnTween,
    destroy() {
      wiggle.destroy();
      shadow.destroy();
    },
  };
}

export type GeodeView = ReturnType<typeof createGeodeView>;
